import React, { createContext, useState, useEffect, useContext } from "react";
import { Bounce, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { db } from "../services/firebase";
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  addDoc,
  collection,
} from "firebase/firestore";
import { useAuth } from "./AuthContext";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [cartItems, setCartItems] = useState(() => {
    const savedCartItems = localStorage.getItem("cartItems");
    return savedCartItems ? JSON.parse(savedCartItems) : [];
  });

  useEffect(() => {
    if (currentUser) {
      const fetchCartItems = async () => {
        try {
          const cartRef = doc(db, "carts", currentUser.uid);
          const cartDoc = await getDoc(cartRef);
          if (cartDoc.exists()) {
            setCartItems(cartDoc.data().items || []);
          }
        } catch (error) {
          console.error("Error fetching cart:", error);
        }
      };

      fetchCartItems();
    }
  }, [currentUser]);

  useEffect(() => {
    // Save cart to Firestore or localStorage
    const saveCart = async (items) => {
      if (currentUser) {
        try {
          const cartRef = doc(db, "carts", currentUser.uid);
          await setDoc(cartRef, { items });
          // console.log("Cart saved to database:", items);
        } catch (error) {
          console.error("Error saving cart to database:", error);
        }
      } else {
        localStorage.setItem("cartItems", JSON.stringify(items));
        // console.log("Cart saved to localStorage:", items);
      }
    };
    saveCart(cartItems);
  }, [cartItems, currentUser]);

  const notifyAdd = () =>
    toast.success("Product Added to Cart", {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
    });

  const addToCart = (product) => {
    notifyAdd();
    setCartItems((prevItems) => {
      const existingProduct = prevItems.find((item) => item.id === product.id);

      if (existingProduct) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      } else {
        return [...prevItems, { ...product, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== productId),
    );
  };

  const increaseQuantity = (productId) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId ? { ...item, quantity: item.quantity + 1 } : item,
      ),
    );
  };

  const decreaseQuantity = (productId) => {
    setCartItems((prevItems) => {
      const product = prevItems.find((item) => item.id === productId);
      if (product.quantity === 1) {
        return prevItems.filter((item) => item.id !== productId);
      } else {
        return prevItems.map((item) =>
          item.id === productId
            ? { ...item, quantity: item.quantity - 1 }
            : item,
        );
      }
    });
  };

  const clearCart = async () => {
    setCartItems([]);
    if (currentUser) {
      try {
        await deleteDoc(doc(db, "carts", currentUser.uid));
      } catch (error) {
        console.error("Error clearing cart from database:", error);
      }
    }
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getSubTotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0,
    );
  };

  const getShippingFees = () => {
    return cartItems.reduce(
      (total, item) => total + item.shippingFee * item.quantity,
      0,
    );
  };

  const getTotalPrice = () => {
    return getShippingFees() + getSubTotal();
  };

  const generateOrderId = () => {
    const prefix = "ORD";
    const randomNum = Math.floor(100000 + Math.random() * 900000);
    return `${prefix}-${randomNum}`;
  };

  const createOrder = async (orderData, clearCartImmediately = false) => {
    try {
      const orderId = generateOrderId();

      // Check if it's COD
      const isCOD = orderData.paymentMethod === "Cash on Delivery";

      const order = {
        orderId,
        ...orderData,
        status: isCOD ? "pending_payment" : "pending",
        paymentStatus: isCOD ? "pending" : "unpaid",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Save to Firebase/Firestore
      if (currentUser) {
        // Option 1: Use auto-generated ID
        await addDoc(collection(db, "orders"), order);

        // Option 2: Or use custom ID
        // await setDoc(doc(db, "orders", orderId), order);
        // console.log("Order saved with custom ID:", orderId);
      } else {
        // Save to localStorage for guest users
        const existingOrders = JSON.parse(
          localStorage.getItem("guestOrders") || "[]",
        );
        existingOrders.push(order);
        localStorage.setItem("guestOrders", JSON.stringify(existingOrders));
        // console.log("Order saved to localStorage for guest");
      }

      // For online payments, save cart backup before clearing (if needed)
      if (!isCOD && !clearCartImmediately) {
        // Save current cart items for potential restoration
        const cartBackup = {
          items: cartItems,
          timestamp: new Date().toISOString(),
          orderId: orderId,
        };
        localStorage.setItem(
          `cart_backup_${orderId}`,
          JSON.stringify(cartBackup),
        );
        // console.log("Cart backup saved for order:", orderId);
      }

      // Clear cart based on parameter or COD status
      if ((clearCartImmediately || isCOD) && clearCart) {
        // console.log("Clearing cart for order:", orderId);
        await clearCart();
      } else {
        // console.log(
        //   "Cart NOT cleared for order:",
        //   orderId,
        //   "- waiting for payment confirmation",
        // );
      }

      return { orderId, ...order };
    } catch (error) {
      console.error("Error creating order:", error);

      // // Restore cart from backup if order creation fails
      // if (!isCOD) {
      //   const backupKey = `cart_backup_${orderId}`;
      //   const backup = localStorage.getItem(backupKey);
      //   if (backup) {
      //     const cartBackup = JSON.parse(backup);
      //     setCartItems(cartBackup.items);
      //     localStorage.setItem("cart", JSON.stringify(cartBackup.items));
      //     console.log("Cart restored from backup after order creation failed");
      //   }
      // }

      throw error;
    }
  };
  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        increaseQuantity,
        decreaseQuantity,
        getTotalPrice,
        getTotalItems,
        clearCart,
        getShippingFees,
        getSubTotal,
        createOrder,
      }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
